import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CheckCircle2,
  ChevronDown,
  Loader2,
  MessageCircle,
  Plug,
} from 'lucide-react'
import {
  getIntegrationsForRole,
  IntegrationRenderer,
} from '@/utils/integration-registry.jsx'
import { getWhatsappInstance } from '@/services/whatsappInstances'
import { getEvolutionInstance } from '@/services/evolution'
import { COLORS } from '../../shared'

const CHANNEL_TYPES = ['whatsapp', 'evolution']

function hasItems(result) {
  const data = result?.data
  if (Array.isArray(data)) return data.length > 0
  return Boolean(data && (data.id || Object.keys(data).length))
}

function IntegrationDetails({ item, assistantID }) {
  if (!item) return null

  return (
    <div
      className="onboarding-setup-integration overflow-hidden rounded-xl p-3"
      style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.07)' }}
    >
      <IntegrationRenderer type={item.type} assistantID={assistantID} />
      <style>{`
        .onboarding-setup-integration :where(.ant-card) { background: transparent !important; border-color: rgba(255,255,255,0.08) !important; box-shadow: none !important; }
        .onboarding-setup-integration :where(.ant-card-head) { border-bottom-color: rgba(255,255,255,0.08) !important; }
        .onboarding-setup-integration :where(.ant-typography, .ant-form-item-label > label) { color: rgba(245,240,250,0.94) !important; }
        .onboarding-setup-integration :where(.ant-typography-secondary, .ant-form-item-explain, .ant-form-item-extra) { color: rgba(255,255,255,0.52) !important; }
      `}</style>
    </div>
  )
}

function ChannelStatus({ assistantID, channels, onChannelStatusChange }) {
  const [selectedType, setSelectedType] = useState(channels[0]?.type)
  const [status, setStatus] = useState({ whatsapp: false, evolution: false })
  const [checking, setChecking] = useState(true)

  const refreshStatus = useCallback(async () => {
    if (!assistantID) return
    const [whatsapp, evolution] = await Promise.allSettled([
      getWhatsappInstance({ assistantID }),
      getEvolutionInstance({ assistantID }),
    ])
    const next = {
      whatsapp: whatsapp.status === 'fulfilled' && hasItems(whatsapp.value),
      evolution: evolution.status === 'fulfilled' && hasItems(evolution.value),
    }
    setStatus(next)
    setChecking(false)
    const connected = channels.find((item) => next[item.type])
    onChannelStatusChange?.({
      connected: Boolean(connected),
      type: connected?.type || null,
      label: connected?.label || null,
    })
  }, [assistantID, channels, onChannelStatusChange])

  useEffect(() => {
    const initialRefresh = window.setTimeout(refreshStatus, 0)
    const interval = window.setInterval(refreshStatus, 5000)
    return () => {
      window.clearTimeout(initialRefresh)
      window.clearInterval(interval)
    }
  }, [refreshStatus])

  const selected = channels.find((item) => item.type === selectedType) || channels[0]
  const connected = channels.find((item) => status[item.type])

  if (!selected) {
    return <p className="text-xs" style={{ color: COLORS.textMuted }}>Nenhum canal está disponível para seu perfil.</p>
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {channels.map((channel) => {
          const active = selected?.type === channel.type
          const done = status[channel.type]
          return (
            <button
              key={channel.type}
              type="button"
              onClick={() => setSelectedType(channel.type)}
              className="flex items-center gap-3 rounded-xl p-3 text-left"
              style={{
                background: active ? 'rgba(52,254,173,0.1)' : 'rgba(255,255,255,0.025)',
                border: active ? '1px solid rgba(52,254,173,0.42)' : '1px solid rgba(255,255,255,0.07)',
              }}
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ background: done ? 'rgba(52,254,173,0.16)' : 'rgba(52,254,173,0.1)', color: '#34FEAD' }}>
                {done ? <CheckCircle2 className="h-4 w-4" /> : <MessageCircle className="h-4 w-4" />}
              </span>
              <span className="min-w-0">
                <span className="block text-xs font-semibold" style={{ color: COLORS.text }}>{channel.label}</span>
                <span className="mt-0.5 block text-[10px] leading-relaxed" style={{ color: COLORS.textMuted }}>
                  {done ? 'Canal conectado' : channel.description}
                </span>
              </span>
            </button>
          )
        })}
      </div>

      <IntegrationDetails item={selected} assistantID={assistantID} />

      <div className="flex items-center gap-2 rounded-xl px-3 py-2.5" style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}>
        {checking ? <Loader2 className="h-3.5 w-3.5 animate-spin text-white/45" /> : connected ? <CheckCircle2 className="h-3.5 w-3.5 text-[#34FEAD]" /> : <MessageCircle className="h-3.5 w-3.5 text-white/45" />}
        <span className="text-[11px]" style={{ color: connected ? '#B6F7D7' : COLORS.textMuted }}>
          {checking ? 'Verificando canais conectados…' : connected ? `${connected.label} conectado.` : 'Conectar um canal é opcional nesta etapa.'}
        </span>
      </div>
    </div>
  )
}

export default function IntegrationsSetup({
  assistantID,
  user,
  mode,
  focusedIntegration,
  onChannelStatusChange,
  onTrack,
}) {
  const available = useMemo(() => getIntegrationsForRole(user?.role), [user?.role])
  const channels = useMemo(() => available.filter((item) => CHANNEL_TYPES.includes(item.type)), [available])
  const catalog = useMemo(() => available.filter((item) => !CHANNEL_TYPES.includes(item.type)), [available])
  const [selectedType, setSelectedType] = useState(null)

  useEffect(() => {
    if (mode !== 'catalog') return
    const requested = catalog.find((item) => item.type === focusedIntegration)
    if (requested) {
      const timer = window.setTimeout(() => setSelectedType(requested.type), 0)
      return () => window.clearTimeout(timer)
    }
    if (!catalog.some((item) => item.type === selectedType)) {
      const timer = window.setTimeout(() => setSelectedType(catalog[0]?.type || null), 0)
      return () => window.clearTimeout(timer)
    }
  }, [catalog, focusedIntegration, mode, selectedType])

  if (mode === 'channels') {
    return (
      <ChannelStatus
        assistantID={assistantID}
        channels={channels}
        onChannelStatusChange={onChannelStatusChange}
      />
    )
  }

  const selected = catalog.find((item) => item.type === selectedType)

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="text-sm font-semibold" style={{ color: COLORS.text }}>
          Integrações disponíveis para seu perfil
        </p>
        <p className="mt-1 text-[11px] leading-relaxed" style={{ color: COLORS.textMuted }}>
          Selecione uma integração para abrir o instalador sem sair do onboarding.
        </p>
      </div>

      {catalog.length === 0 ? (
        <p className="rounded-xl border border-dashed px-4 py-6 text-center text-xs" style={{ borderColor: 'rgba(255,255,255,0.12)', color: COLORS.textMuted }}>
          Nenhuma integração adicional está disponível para seu perfil.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {catalog.map((item) => {
            const active = item.type === selectedType
            return (
              <button
                key={item.type}
                type="button"
                onClick={() => {
                  setSelectedType(item.type)
                  onTrack?.('onboarding_integration_opened')
                }}
                className="flex items-center gap-3 rounded-xl p-3 text-left transition-colors"
                style={{
                  background: active ? 'rgba(254,52,173,0.1)' : 'rgba(255,255,255,0.025)',
                  border: active ? '1px solid rgba(254,52,173,0.42)' : '1px solid rgba(255,255,255,0.07)',
                }}
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ background: 'rgba(254,52,173,0.13)', color: '#FE75C0' }}>
                  {item.icon || <Plug className="h-4 w-4" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-xs font-semibold" style={{ color: COLORS.text }}>{item.label}</span>
                  <span className="mt-0.5 block line-clamp-2 text-[10px] leading-relaxed" style={{ color: COLORS.textMuted }}>{item.description}</span>
                </span>
                <ChevronDown className="h-3.5 w-3.5 shrink-0 text-white/35" style={{ transform: active ? 'rotate(180deg)' : undefined }} />
              </button>
            )
          })}
        </div>
      )}

      {selected && <IntegrationDetails item={selected} assistantID={assistantID} />}
    </div>
  )
}
